import * as Yup from 'yup';
import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { ChevronDown, Search } from '@withjoy/joykit/icons';
import { Form, Formik, useField, useFormikContext } from 'formik';
import { Box, ButtonV2, CheckboxV2, Flex, InputV2, TextV2, styled } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { isValidCoordinates } from '@shared/core/places';
import { DRIVER_AGE_RANGE } from '../../CarRentalConsole.constants';
import { LocationInputV2 } from '@shared/components/LocationInputV2';
import { CalendarRangePicker } from '@shared/components/CalendarRangePicker';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { formatDateToYmd, parseDriverAge, parseYmdToDate } from '../../CarRentalConsole.utils';
import type { CarSearchLocation, CarSearchState, CarSearchTerms } from '../../CarRentalConsole.types';
import type { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';

type CarSearchBarProps = Readonly<{
  state: CarSearchState;
  isGeocoding?: boolean;
  pendingDestination?: string;
  onSubmit: (value: CarSearchTerms) => void;
  layout?: 'default' | 'mobileSheet';
}>;

type LocationValue = Readonly<{
  lat?: number;
  lng?: number;
  city?: string;
  address?: string;
  displayName?: string;
  googlePlaceId?: string;
}>;

type FormValues = Readonly<{
  pickUpTime: string;
  dropOffTime: string;
  driverAge: string;
  driverAgeApplicable: boolean;
  pickUpLocation: LocationValue;
  dropOffLocation: LocationValue;
  dates: Readonly<[Date | undefined, Date | undefined]>;
}>;

type SelectOption = Readonly<{ label: string; value: string }>;

const useTimeOptions = (): ReadonlyArray<SelectOption> => {
  const { formatTime } = useEventLocaleFormatter();

  return useMemo(
    () =>
      Array.from({ length: 48 }, (_, index) => {
        const hours = Math.floor(index / 2);
        const minutes = index % 2 === 0 ? 0 : 30;
        const value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        const label = formatTime(new Date(2000, 0, 1, hours, minutes), 'h:mm') ?? value;
        return { label, value };
      }),
    [formatTime],
  );
};

const locationValueSchema = Yup.object({
  lat: Yup.number(),
  lng: Yup.number(),
  displayName: Yup.string(),
  googlePlaceId: Yup.string(),
});

const schema = Yup.object({
  pickUpTime: Yup.string().required(),
  dropOffTime: Yup.string().required(),
  driverAge: Yup.string(),
  driverAgeApplicable: Yup.boolean(),
  dropOffLocation: locationValueSchema,
  dates: Yup.array().test('dates', 'Invalid dates', value => Array.isArray(value) && !!value[0] && !!value[1]),
  pickUpLocation: locationValueSchema.test('pickUpLocation', 'Invalid pick-up location', value => !!value && typeof value.lat === 'number' && typeof value.lng === 'number'),
});

const StyledFieldControl = styled(Box)`
  width: 100%;

  & > div {
    min-width: 0;
  }

  /* Compact, flush-left value — matches the Dates field. Right padding is set
     inline by InputV1 to reserve room for the chevron, so we leave it alone. */
  & input {
    min-height: ${pxToRem(22)};
    height: ${pxToRem(22)};
    padding-left: 0 !important;
    background-color: transparent;
  }

  /* The trailing affordances (× clear, chevron) live in an absolutely-positioned
     InputV1 icon container that defaults to min-height: 48px. Once the input is
     compact that 48px floor drops the icon ~13px below the value, so pin the icon
     container to the input height. Scoped to the input's sibling icon span so it
     never touches the dropdown menu. */
  & input ~ span {
    min-height: ${pxToRem(22)};
    height: ${pxToRem(22)};
  }
`;

const toCarSearchLocation = (value: LocationValue): CarSearchLocation => ({
  lat: value.lat,
  lng: value.lng,
  city: value.city,
  address: value.address,
  displayName: value.displayName,
  googlePlaceId: value.googlePlaceId,
});

const FIELD_HEIGHT = pxToRem(56);

const isDriverAgeEntryInvalid = (values: Pick<FormValues, 'driverAge' | 'driverAgeApplicable'>): boolean => {
  if (!values.driverAgeApplicable) return false;
  const raw = values.driverAge.trim();
  return raw !== '' && parseDriverAge(raw) === undefined;
};

const isSearchReady = (values: Pick<FormValues, 'dates' | 'pickUpLocation' | 'driverAge' | 'driverAgeApplicable'>) => {
  const [start, end] = values.dates;
  return isValidCoordinates({ latitude: values.pickUpLocation.lat, longitude: values.pickUpLocation.lng }) && !!start && !!end && !isDriverAgeEntryInvalid(values);
};

const FieldBox = ({ label, grow = 1, children }: { label: string; grow?: number; children: React.ReactNode }) => (
  <Flex
    rowGap={1}
    paddingX={4}
    minWidth={0}
    background="white"
    border="solid 1px"
    borderColor="mono3"
    height={FIELD_HEIGHT}
    flexDirection="column"
    justifyContent="center"
    borderRadius={pxToRem(12)}
    flex={{ md: `${grow} 1 0` }}
    width={{ _: '100%', md: 'auto' }}
  >
    <TextV2
      color="mono10"
      whiteSpace="nowrap"
      typographyVariant="label3"
    >
      {label}
    </TextV2>
    {children}
  </Flex>
);

const LocationField = ({ fieldId, placeholder, disabled }: { fieldId: string; placeholder: string; disabled?: boolean }) => {
  const [{ value }, , helper] = useField<LocationValue>(fieldId);

  const onChange = (response: LocationInputV2Response | null) => {
    if (!response) {
      helper.setValue({ displayName: '', city: undefined, address: undefined, lat: undefined, lng: undefined, googlePlaceId: '' });
      return;
    }
    helper.setValue({
      lat: response.lat,
      lng: response.lng,
      city: response.city,
      googlePlaceId: response.placeId,
      displayName: response.displayName,
      address: response.formattedAddress ?? response.address,
    });
  };

  return (
    <StyledFieldControl>
      <LocationInputV2
        noBorder
        name={fieldId}
        iconRight={null}
        disabled={disabled}
        includeMapIcon={false}
        includeHotelIcon={false}
        placeholder={placeholder}
        placePredictionTypes={[]}
        isHideListItemIcon={true}
        onLocationChange={onChange}
        defaultValue={value.displayName}
      />
    </StyledFieldControl>
  );
};

const DatesField = ({ disabled }: { disabled?: boolean }) => {
  const [field, , helper] = useField<FormValues['dates']>('dates');

  const { tq } = useTranslation('carRentalConsole');
  const { formatDateRange } = useEventLocaleFormatter();

  const dateRangeDisplay = formatDateRange(field.value[0], field.value[1]);
  const display = <FakeInput disabled={disabled}>{dateRangeDisplay || tq`Select dates`}</FakeInput>;

  if (disabled) {
    return display;
  }

  return (
    <CalendarRangePicker
      closeOnSelectDate
      initial={field.value}
      onChange={([start, end]) => helper.setValue([start, end])}
    >
      {display}
    </CalendarRangePicker>
  );
};

const FakeInput = React.forwardRef<HTMLDivElement, { children: React.ReactNode; disabled?: boolean }>(({ children, disabled, ...restProps }, ref) => (
  <Box
    ref={ref}
    {...restProps}
    color="mono14"
    overflow="hidden"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    typographyVariant="body1"
    aria-disabled={disabled || undefined}
    cursor={disabled ? 'not-allowed' : 'pointer'}
  >
    {children}
  </Box>
));
FakeInput.displayName = 'FakeInput';

const TimeChevron = styled(Box)`
  top: 50%;
  right: 0;
  display: flex;
  position: absolute;
  pointer-events: none;
  align-items: center;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
`;

const TimeSelect = styled.select`
  padding: 0;
  width: 100%;
  border: none;
  font: inherit;
  outline: none;
  cursor: pointer;
  appearance: none;
  -moz-appearance: none;
  height: ${pxToRem(22)};
  -webkit-appearance: none;
  font-size: ${pxToRem(16)};
  padding-right: ${pxToRem(20)};
  background-color: transparent;
  color: ${props => props.theme.colors.mono14};
`;

const TimeSelectWrapper = styled(Box)`
  width: 100%;
  display: flex;
  position: relative;
  align-items: center;
`;

const TimeField = ({ fieldId, label, options, disabled }: { fieldId: string; label: string; options: ReadonlyArray<SelectOption>; disabled?: boolean }) => {
  const [field, , helper] = useField<string>(fieldId);

  return (
    <TimeSelectWrapper>
      <TimeSelect
        aria-label={label}
        value={field.value}
        disabled={disabled}
        onChange={event => helper.setValue(event.target.value)}
      >
        {options.map(option => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </TimeSelect>
      <TimeChevron color="mono10">
        <ChevronDown size={20} />
      </TimeChevron>
    </TimeSelectWrapper>
  );
};

const DriverAgeField = ({ disabled }: { disabled?: boolean }) => {
  const { tq } = useTranslation('carRentalConsole');
  const [applicableField, , applicableHelper] = useField<boolean>('driverAgeApplicable');
  const [ageField, , ageHelper] = useField<string>('driverAge');

  const toggle = () => {
    if (disabled) return;
    const next = !applicableField.value;
    applicableHelper.setValue(next);
    if (!next) ageHelper.setValue('');
  };

  const showAgeError = isDriverAgeEntryInvalid({ driverAge: ageField.value, driverAgeApplicable: applicableField.value });

  return (
    <Flex
      rowGap={2}
      flexDirection="column"
    >
      <TextV2
        color="mono14"
        typographyVariant="label3"
      >
        {tq`Driver age`}
      </TextV2>
      <Flex
        gap={4}
        flexWrap="wrap"
        alignItems="flex-start"
      >
        <Box>
          <CheckboxV2
            onChange={toggle}
            id="driverAgeApplicable"
            isDisabled={disabled}
            isChecked={applicableField.value}
          >
            <TextV2
              color="mono14"
              typographyVariant="body1"
            >
              {tq`Driver under 30 or over 70 years old`}
            </TextV2>
          </CheckboxV2>
          <TextV2
            color="mono12"
            marginTop={1}
            typographyVariant="label2"
          >
            {tq`Young or senior drivers may be required to pay an additional fee.`}
          </TextV2>
        </Box>
        {applicableField.value && (
          <Box
            flexShrink={0}
            width={pxToRem(96)}
          >
            <InputV2
              type="number"
              placeholder={tq`Age`}
              value={ageField.value}
              isDisabled={disabled}
              aria-label={tq`Driver age`}
              isInvalid={showAgeError}
              onChange={event => ageHelper.setValue(event.target.value)}
            />
          </Box>
        )}
      </Flex>
      {showAgeError && (
        <TextV2
          color="negative6"
          typographyVariant="label2"
        >
          {tq`Enter an age between ${DRIVER_AGE_RANGE.MIN} and ${DRIVER_AGE_RANGE.MAX}.`}
        </TextV2>
      )}
    </Flex>
  );
};

const SearchButton = ({ isGeocoding }: { isGeocoding?: boolean }) => {
  const { tq } = useTranslation('carRentalConsole');
  const { values } = useFormikContext<FormValues>();

  return (
    <ButtonV2
      type="submit"
      shape="rounded"
      variant="solid"
      intent="primary"
      height={FIELD_HEIGHT}
      loading={isGeocoding}
      disabled={!isSearchReady(values)}
      width={{ _: '100%', md: 'auto' }}
      startIcon={<Search size={20} />}
      aria-label={isGeocoding ? tq`Locating pick-up location` : tq`Search`}
    >
      {tq`Search`}
    </ButtonV2>
  );
};

export const CarSearchBar = ({ state, onSubmit, layout = 'default', pendingDestination, isGeocoding }: CarSearchBarProps) => {
  const { tq } = useTranslation('carRentalConsole');
  const isMobileSheet = layout === 'mobileSheet';

  const timeOptions = useTimeOptions();

  const initialValues = useMemo<FormValues>(
    () => ({
      pickUpTime: state.pickUpTime,
      dropOffTime: state.dropOffTime,
      pickUpLocation: {
        lat: state.pickUpLocation?.lat,
        lng: state.pickUpLocation?.lng,
        city: state.pickUpLocation?.city,
        address: state.pickUpLocation?.address,
        googlePlaceId: state.pickUpLocation?.googlePlaceId,
        displayName: state.pickUpLocation?.displayName || pendingDestination,
      },
      dropOffLocation: {
        lat: state.dropOffLocation?.lat,
        lng: state.dropOffLocation?.lng,
        city: state.dropOffLocation?.city,
        address: state.dropOffLocation?.address,
        googlePlaceId: state.dropOffLocation?.googlePlaceId,
        displayName: state.dropOffLocation?.displayName || pendingDestination,
      },
      driverAgeApplicable: state.driverAge != null,
      driverAge: state.driverAge != null ? String(state.driverAge) : '',
      dates: [parseYmdToDate(state.pickUpDate), parseYmdToDate(state.dropOffDate)],
    }),
    [state, pendingDestination],
  );

  const fieldsDisabled = !!isGeocoding;

  const fields = (
    <>
      <FieldBox
        grow={3}
        label={tq`Pick-up`}
      >
        <LocationField
          fieldId="pickUpLocation"
          disabled={fieldsDisabled}
          placeholder={tq`Pick-up location`}
        />
      </FieldBox>
      <FieldBox
        grow={3}
        label={tq`Drop-off`}
      >
        <LocationField
          fieldId="dropOffLocation"
          disabled={fieldsDisabled}
          placeholder={tq`Drop-off location`}
        />
      </FieldBox>
      <FieldBox
        grow={2}
        label={tq`Dates`}
      >
        <DatesField disabled={fieldsDisabled} />
      </FieldBox>
      <FieldBox
        grow={1}
        label={tq`Pick-up time`}
      >
        <TimeField
          fieldId="pickUpTime"
          options={timeOptions}
          label={tq`Pick-up time`}
          disabled={fieldsDisabled}
        />
      </FieldBox>
      <FieldBox
        grow={1}
        label={tq`Drop-off time`}
      >
        <TimeField
          fieldId="dropOffTime"
          options={timeOptions}
          label={tq`Drop-off time`}
          disabled={fieldsDisabled}
        />
      </FieldBox>
    </>
  );

  return (
    <Formik<FormValues>
      enableReinitialize
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={value => {
        const [start, end] = value.dates;

        if (!isValidCoordinates({ latitude: value.pickUpLocation.lat, longitude: value.pickUpLocation.lng }) || !start || !end) {
          return;
        }

        const pickUpLocation = toCarSearchLocation(value.pickUpLocation);
        const dropOffLocation = isValidCoordinates({ latitude: value.dropOffLocation.lat, longitude: value.dropOffLocation.lng })
          ? toCarSearchLocation(value.dropOffLocation)
          : pickUpLocation;

        onSubmit({
          pickUpLocation,
          dropOffLocation,
          pickUpTime: value.pickUpTime,
          dropOffTime: value.dropOffTime,
          dropOffDate: formatDateToYmd(end),
          pickUpDate: formatDateToYmd(start),
          driverAge: value.driverAgeApplicable ? parseDriverAge(value.driverAge) : undefined,
        });
      }}
    >
      <Form style={{ width: '100%', height: isMobileSheet ? '100%' : undefined }}>
        {isMobileSheet ? (
          <Flex
            width="100%"
            minHeight="100%"
            flexDirection="column"
          >
            <Flex
              gap={4}
              padding={4}
              flexDirection="column"
            >
              {fields}
              <DriverAgeField disabled={fieldsDisabled} />
            </Flex>
            <Box
              bottom={0}
              width="100%"
              paddingX={4}
              paddingTop={4}
              flexShrink={0}
              zIndex="sticky"
              marginTop="auto"
              position="sticky"
              background="white"
              borderTop="1px solid"
              borderTopColor="mono3"
              paddingBottom="calc(16px + env(safe-area-inset-bottom, 0px))"
            >
              <SearchButton isGeocoding={isGeocoding} />
            </Box>
          </Flex>
        ) : (
          <Flex
            width="100%"
            gap={{ _: 3, md: 4 }}
            flexDirection="column"
          >
            <Flex
              width="100%"
              gap={{ _: 3, md: 2 }}
              justifyContent="center"
              flexDirection={{ _: 'column', md: 'row' }}
              alignItems={{ _: 'stretch', md: 'center' }}
            >
              {fields}
              <Box
                flexShrink={0}
                width={{ _: '100%', md: 'auto' }}
              >
                <SearchButton isGeocoding={isGeocoding} />
              </Box>
            </Flex>
            <DriverAgeField disabled={fieldsDisabled} />
          </Flex>
        )}
      </Form>
    </Formik>
  );
};
